import { useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const categorySchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '南宫集团产品服务',
  description: '南宫集团产品服务分类展示，涵盖科技研发、产业投资、国际贸易等核心业务板块',
};

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'tech';
  const [activeCategory, setActiveCategory] = useState('all');

  const isNewsTab = currentTab === 'news';

  const handleTabChange = useCallback((tab: string) => {
    setSearchParams({ tab });
    setActiveCategory('all');
  }, [setSearchParams]);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: isNewsTab ? '新闻资讯' : '产品服务' },
  ];

  const currentCategoryData = !isNewsTab ? categories.find((c) => c.id === currentTab) : null;

  return (
    <>
      <SEOHead
        title={isNewsTab ? '新闻资讯' : '产品服务'}
        description={isNewsTab ? '南宫集团最新新闻资讯与行业动态，了解集团发展、产品更新、社会责任等信息。' : '南宫集团产品服务分类展示，涵盖科技研发、产业投资、国际贸易等核心业务板块，了解我们的产品与服务详情。'}
        keywords={isNewsTab ? '南宫集团新闻,南宫集团动态,企业新闻,行业资讯' : '南宫集团产品,南宫集团服务,科技研发,产业投资,国际贸易'}
        schema={categorySchema}
      />

      <div className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="py-8 md:py-12 bg-background-50">
          <div className="site-container px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
              {isNewsTab ? '新闻资讯' : '产品服务'}
            </h1>
            <p className="text-sm text-foreground-600 mb-8">
              {isNewsTab ? '了解南宫集团最新动态与行业资讯' : '探索南宫集团核心业务与产品服务'}
            </p>

            <div className="flex items-center gap-2 mb-8 bg-background-100 rounded-full p-1 max-w-md">
              {[
                { key: 'tech', label: '科技研发' },
                { key: 'invest', label: '产业投资' },
                { key: 'trade', label: '国际贸易' },
                { key: 'news', label: '新闻资讯' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`flex-1 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    (tab.key === 'news' && isNewsTab) || (!isNewsTab && tab.key === currentTab)
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-800'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {isNewsTab ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200 hover:border-primary-200 transition-all duration-300 overflow-hidden cursor-pointer"
                  >
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-500">{article.date}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                        {article.summary}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-foreground-500">
                        <span><i className="ri-eye-line mr-1"></i>{article.readCount.toLocaleString()}</span>
                        <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : currentCategoryData ? (
              <>
                <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-500 flex-shrink-0">
                      <i className={`${currentCategoryData.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-foreground-900 mb-2">{currentCategoryData.name}</h2>
                      <p className="text-sm text-foreground-600 leading-relaxed">{currentCategoryData.description}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentCategoryData.items.map((item) => (
                    <div
                      key={item.id}
                      className="group bg-background-50 rounded-xl border border-background-200 hover:border-accent-200 transition-all duration-300 overflow-hidden cursor-pointer"
                    >
                      <div className="h-48 overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.name}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">{item.description}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {item.tags.map((tag) => (
                            <span key={tag} className="text-xs text-accent-600 bg-accent-50 px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </section>
      </div>
    </>
  );
}